import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaCertificate, FaAward, FaShieldAlt, FaCode, FaGraduationCap } from 'react-icons/fa';
import './Certificates.css';

const Certificates = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  // Your actual certificates with proper formatting
  const certificates = [
    {
      id: 1,
      title: 'Certificate 1',
      issuer: 'Professional Certification',
      date: '2024',
      image: '/certs/c1.png',
      color: '#ff6b6b',
      description: 'Professional development certification'
    },
    {
      id: 2,
      title: 'Certificate 2',
      issuer: 'Technical Excellence',
      date: '2024',
      image: '/certs/c2.png',
      color: '#4ecdc4',
      description: 'Advanced technical skills certification'
    },
    {
      id: 3,
      title: 'Certificate 3',
      issuer: 'Industry Recognition',
      date: '2023',
      image: '/certs/c3.png',
      color: '#45b7d1',
      description: 'Industry standard certification'
    },
    {
      id: 4,
      title: 'Certificate 4',
      issuer: 'Specialized Training',
      date: '2024',
      image: '/certs/c4.png',
      color: '#96ceb4',
      description: 'Specialized skill development'
    },
    {
      id: 5,
      title: 'Certificate 5',
      issuer: 'Professional Growth',
      date: '2023',
      image: '/certs/c5.png',
      color: '#feca57',
      description: 'Professional development achievement'
    },
    {
      id: 6,
      title: 'Certificate 6',
      issuer: 'Technical Mastery',
      date: '2024',
      image: '/certs/c6.png',
      color: '#ff9ff3',
      description: 'Technical expertise certification'
    },
    {
      id: 7,
      title: 'Certificate 7',
      issuer: 'Advanced Skills',
      date: '2024',
      image: '/certs/c7.png',
      color: '#54a0ff',
      description: 'Advanced skill certification'
    },
    {
      id: 8,
      title: 'Certificate 8',
      issuer: 'Excellence Award',
      date: '2023',
      image: '/certs/c8.png',
      color: '#5f27cd',
      description: 'Excellence in performance'
    },
    {
      id: 9,
      title: 'Certificate 9',
      issuer: 'Achievement Recognition',
      date: '2024',
      image: '/certs/c9.png',
      color: '#00d2d3',
      description: 'Outstanding achievement award'
    },
    {
      id: 10,
      title: 'Certificate 10',
      issuer: 'Completion Award',
      date: '2024',
      image: '/certs/c10.png',
      color: '#ff7675',
      description: 'Program completion certificate'
    },
    {
      id: 11,
      title: 'Certificate 11',
      issuer: 'Advanced Certification',
      date: '2024',
      image: '/certs/c11.png',
      color: '#a29bfe',
      description: 'Advanced professional certification'
    },
    {
      id: 12,
      title: 'Certificate 12',
      issuer: 'Expert Level',
      date: '2024',
      image: '/certs/c12.png',
      color: '#fd79a8',
      description: 'Expert level achievement'
    },
    {
      id: 13,
      title: 'Certificate 13',
      issuer: 'Mastery Award',
      date: '2024',
      image: '/certs/c13.png',
      color: '#fdcb6e',
      description: 'Mastery level certification'
    },
    {
      id: 14,
      title: 'Certificate 14',
      issuer: 'Latest Achievement',
      date: '2025',
      image: '/certs/c14.jpeg',
      color: '#00cec9',
      description: 'Latest professional certification'
    }
  ];

  // Duplicate certificates for seamless loop
  const duplicatedCertificates = [...certificates, ...certificates];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="certificates" className="certificates" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Certifications & Achievements
          </motion.h2>

          <motion.p className="certificates-subtitle" variants={itemVariants}>
            Professional certifications and achievements in cybersecurity and technology
          </motion.p>

          <div className="certificates-container">
            <div className="certificates-track">
              {duplicatedCertificates.map((cert, index) => (
                <motion.div
                  key={`${cert.id}-${index}`}
                  className="certificate-card"
                  whileHover={{ 
                    scale: 1.05,
                    rotateY: 5,
                    z: 50
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    '--cert-color': cert.color
                  }}
                >
                  <div className="certificate-glow"></div>
                  <div className="electric-border"></div>
                  <div className="scanning-line"></div>
                  
                  <div className="certificate-image-container">
                    <img 
                      src={cert.image} 
                      alt={cert.title}
                      className="certificate-image"
                      loading="lazy"
                    />
                    <div className="certificate-overlay">
                      <div className="certificate-info">
                        <h3 className="certificate-title">{cert.title}</h3>
                        <p className="certificate-issuer">{cert.issuer}</p>
                        <p className="certificate-date">{cert.date}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="certificate-badge">
                    <FaAward />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div className="certificates-stats" variants={itemVariants}>
            <div className="stats-container">
              <motion.div 
                className="stat-item"
                whileHover={{ scale: 1.05 }}
              >
                <div className="stat-number">14+</div>
                <div className="stat-label">Certifications</div>
              </motion.div>
              <motion.div 
                className="stat-item"
                whileHover={{ scale: 1.05 }}
              >
                <div className="stat-number">4+</div>
                <div className="stat-label">Years Learning</div>
              </motion.div>
              <motion.div 
                className="stat-item"
                whileHover={{ scale: 1.05 }}
              >
                <div className="stat-number">100%</div>
                <div className="stat-label">Commitment</div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div className="certificates-cta" variants={itemVariants}>
            <h3>Continuous Learning</h3>
            <p>Always expanding my knowledge and staying updated with the latest in cybersecurity</p>
            <motion.button 
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View All Credentials
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Certificates;